import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { LOGO_URL } from "../lib/brand";
import { QACommunityBanner } from "../components/QACommunity";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";
import {
  Check, Lock, Phone, Mail, ChevronRight, BookOpen, BarChart3,
  FileText, Wrench, MessagesSquare, LogOut, Sparkles, ExternalLink, Copy,
  MessageCircle, Lightbulb, Trophy, CreditCard, Building2, Bitcoin, LayoutDashboard,
  Smartphone, Share, Plus, Clock, ArrowRight, AlertTriangle,
} from "lucide-react";
import { RF_VIDEO_ID, RF_STEPS, RF_REGISTER_LINK, RF_WHATSAPP_CHANNEL } from "../lib/roboforexOnboarding";

const FALLBACK_PHONE = "+41 79 950 16 46";
const FALLBACK_PHONE_TEL = "+41799501646";

// MT5 account numbers from RoboForex are purely numeric, typically 7–9 digits
// (real-world example: 37320083). We accept 6–12 digits to stay robust against
// broker-side format tweaks without letting empty/placeholder values through.
const MT5_MIN = 6;
const MT5_MAX = 12;
const isMt5Valid = (s) => {
  const v = String(s || "").trim();
  return /^[0-9]+$/.test(v) && v.length >= MT5_MIN && v.length <= MT5_MAX;
};
const EMAIL = "kontakt@moonshine-solutions.com";
const WHATSAPP_CHANNEL = RF_WHATSAPP_CHANNEL;

const VIDEO1_ID = RF_VIDEO_ID;
const VIDEO2_ID = "_9ujT5rbg-4";

const VIDEO1_LEN = 7;
const VIDEO2_LEN = 5;

// Build phone digits-only for wa.me / tel: links
function phoneToWaLink(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^0-9]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}
function phoneToTel(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^0-9+]/g, "");
  return digits ? `tel:${digits}` : null;
}

const REGISTER_LINK = RF_REGISTER_LINK;

// Checklist items — index aligned with backend video1_checks (7) / video2_checks (22)
const VIDEO1_STEPS = RF_STEPS;

const VIDEO2_STEPS = [
  {
    title: "Account erstellen",
    text: `"Accounts" → "Open a New Account" → "Real Account" → "MetaTrader 5" → Typ "MT5 ProCent" wählen.`,
  },
  {
    title: "Einstellungen & Partners Agent ID",
    text: `Währung USD, Hebel 1:1000 belassen → Passwort erstellen & abspeichern → unter "Partners Agent ID" den Partner-Slug deines Empfehlungsgebers eintragen (siehe Hinweis darunter) → "Confirm" klicken.`,
  },
  {
    title: "Account-Nummer notieren",
    text: "MT5 Account-Nummer sofort notieren und unten eintragen (wichtig für die Zuordnung).",
  },
  {
    title: "Geld auf MT5 Konto einzahlen",
    text: `Startseite → Button "Einzahlen" beim MT5 ProCent → Einzahlen via Kreditkarte/Banküberweisung/Crypto.`,
  },
  {
    title: "Bot verknüpfen via Copy-Trading-Link",
    text: `Öffne den persönlichen Copy-Trading-Link deines Empfehlungsgebers (Button unten) → wähle deinen neu erstellten MT5 ProCent-Account → "Proportional" + Koeffizient "1" → Verbindung bestätigen. Damit folgst du unserem AlphaLine-Bot.`,
  },
];

// Sonder-Onboarding NUR für Kunden mit Referral "gubnr" (Moonshine Solutions):
// eigenes Video 2 + manueller Copy-Trading-Schritt statt persönlichem Link.
// Für ALLE anderen Referrals bleibt das Standard-Onboarding unverändert.
const GUBNR_SLUG = "gubnr";
const GUBNR_VIDEO2_ID = "ZZ8JJRP6B9k";
const GUBNR_STEP5 = {
  title: "Bot verknüpfen via Copy-Trading",
  text: `„Copy Trading" öffnen → Nach „AlphalaneAI" suchen → „Alpha_ai_v2" auswählen → „Send request" klicken → "Proportional" + Koeffizient "1" → Verbindung bestätigen. Damit folgst du unserem Alphalane-Bot.`,
};

export default function Onboarding() {
  const { user, logout, patchProgress } = useAuth();
  const navigate = useNavigate();
  const [video1Checks, setVideo1Checks] = useState(() => Array(VIDEO1_LEN).fill(false));
  const [video2Checks, setVideo2Checks] = useState(() => Array(VIDEO2_LEN).fill(false));
  const [mt5, setMt5] = useState("");
  const [savingProgress, setSavingProgress] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const video2Ref = useRef(null);
  const completionRef = useRef(null);

  // Redirect if not logged in. If the customer is approved but hasn't signed
  // the Dienstleistungsvertrag yet AND hasn't been waived by the admin, route
  // them through the contract step first.
  useEffect(() => {
    if (user === false) navigate("/login", { replace: true, state: { from: "/onboarding" } });
    if (user && user.id && !user?.contract?.signed_at && !user?.contract?.waived) {
      const token = (typeof window !== "undefined" && localStorage.getItem("alphalane_contract_token")) || "";
      navigate(`/onboarding/contract${token ? `?token=${encodeURIComponent(token)}` : ""}`, { replace: true });
    }
  }, [user, navigate]);

  usePageSEO({ title: "Onboarding · Alphalane", noIndex: true });

  // Hydrate ONLY ONCE per session (when the user.id first appears) — otherwise
  // every server-side echo (after every PATCH) would override the user's local
  // typing/clicks and the UI feels like nothing is being saved.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!user || !user.id) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const p = user.onboarding_progress || {};
    setVideo1Checks(p.video1_checks?.length === VIDEO1_LEN ? p.video1_checks : Array(VIDEO1_LEN).fill(false));
    // Migrate any older video2 progress (8, 21, 22 items, etc.) to the new
    // 5-step structure. We just reset progress in those cases — the user can
    // re-check the 5 condensed steps in a few seconds.
    const v2 = p.video2_checks;
    setVideo2Checks(Array.isArray(v2) && v2.length === VIDEO2_LEN ? v2 : Array(VIDEO2_LEN).fill(false));
    setMt5(user.mt5_account || "");
  }, [user]);

  const partnerSlug = user?.partner_slug || "";
  const partnerName = user?.partner_name || "";
  const partnerPhone = user?.partner_phone || "";
  const partnerEmail = user?.partner_email || "";
  // Partner's individual MT5/Copy-Trading "Partners Agent ID" — set per
  // partner. Customers of that partner see THIS value under Video 2 of the
  // onboarding flow (NOT their personal partner_slug, which is for Video 1).
  // Empty string ⇒ the partner hasn't filled in this field yet → we surface a
  // help-message in the banner instead of displaying a fake ID.
  const partnerMt5Id = (user?.partner_mt5_id || "").trim();

  // Gubnr-Sonderfall (Moonshine Solutions): eigenes Video 2 + manueller
  // Copy-Trading-Schritt; der persönliche Copy-Trading-Link-Banner entfällt.
  const isGubnr = (partnerSlug || "").trim().toLowerCase() === GUBNR_SLUG;
  const video2Id = isGubnr ? GUBNR_VIDEO2_ID : VIDEO2_ID;
  const video2Steps = isGubnr ? [...VIDEO2_STEPS.slice(0, 4), GUBNR_STEP5] : VIDEO2_STEPS;

  // For "Kontakt zum Empfehlungsgeber" — fall back to default if no partner phone set
  const contactPhone = partnerPhone || FALLBACK_PHONE;
  const contactWa = phoneToWaLink(partnerPhone) || phoneToWaLink(FALLBACK_PHONE);
  const contactTel = phoneToTel(partnerPhone) || `tel:${FALLBACK_PHONE_TEL}`;
  const contactLabel = partnerName || "Dein Empfehlungsgeber";

  const video1Done = video1Checks.every(Boolean);
  const video2Done = video2Checks.every(Boolean);
  const mt5Valid = isMt5Valid(mt5);
  const totalSteps = video1Checks.length + video2Checks.length;
  const completedSteps = video1Checks.filter(Boolean).length + video2Checks.filter(Boolean).length;
  const percent = Math.round((completedSteps / totalSteps) * 100);
  const allDone = video1Done && video2Done && mt5Valid;

  const motivationText = useMemo(() => {
    if (allDone) return "Alles geschafft! Herzlichen Glückwunsch 🎉";
    if (percent >= 80) return "Fast geschafft — bleib dran!";
    if (percent >= 50) return "Halbzeit — du machst das super.";
    if (percent >= 20) return "Schon gut unterwegs — weiter so!";
    if (percent > 0) return "Erster Schritt gemacht. Auf geht's!";
    return "Bereit? Lass uns Schritt für Schritt durchgehen.";
  }, [percent, allDone]);

  // Auto-save progress (debounced) + immediate flush helper.
  const saveTimer = useRef(null);
  const pendingPayload = useRef(null);

  const flushSaveImmediate = useCallback(async () => {
    if (!pendingPayload.current) return;
    const payload = pendingPayload.current;
    pendingPayload.current = null;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    try {
      await patchProgress(payload);
    } catch (err) {
      log.warn("[onboarding] flush failed (will retry on next debounce):", err);
    }
  }, [patchProgress]);

  const saveProgress = useCallback((next1, next2, nextMt5) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const payload = { video1_checks: next1, video2_checks: next2, mt5_account: nextMt5 };
    pendingPayload.current = payload;
    setSavingProgress(true);
    saveTimer.current = setTimeout(async () => {
      const p = pendingPayload.current;
      pendingPayload.current = null;
      saveTimer.current = null;
      try {
        await patchProgress(p);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401) {
          toast.error("Session abgelaufen. Bitte neu einloggen.");
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        } else if (status === 400) {
          toast.error(`Eingabefehler: ${detail || "ungültige Daten"}`);
        } else {
          toast.error(`Speichern fehlgeschlagen (${status || "Netzwerk"}): ${detail || err?.message || "Unbekannt"}`);
        }
        log.error("[onboarding] patch failed:", { status, detail, err });
      } finally {
        setSavingProgress(false);
      }
    }, 600);
  }, [patchProgress, navigate]);

  // Mirror live state into a ref so effects can grab the *current* values
  // without bloating their dependency arrays (which would re-trigger the
  // effect on every toggle/keystroke and cause stale-closure bugs).
  const liveRef = useRef({ video1Checks, video2Checks, mt5 });
  liveRef.current = { video1Checks, video2Checks, mt5 };

  // Reliability net: if the user closes the tab / navigates away while a save
  // is still pending in the debounce window (the classic "I typed my MT5 and
  // left before 600ms" footgun), flush synchronously via sendBeacon so the
  // number always lands on the server.
  useEffect(() => {
    const onLeave = () => {
      if (!pendingPayload.current) return;
      try {
        const url = `${BACKEND_URL}/api/auth/me/onboarding`;
        const blob = new Blob([JSON.stringify(pendingPayload.current)], { type: "application/json" });
        // Beacon doesn't carry auth headers — but the auth cookie does ride along
        // because navigator.sendBeacon includes credentials by default.
        navigator.sendBeacon(url, blob);
      } catch (err) {
        log.warn("[onboarding] beacon flush failed:", err);
      }
    };
    window.addEventListener("beforeunload", onLeave);
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("beforeunload", onLeave);
      window.removeEventListener("pagehide", onLeave);
    };
  }, []);

  const toggle1 = (i) => () => {
    const next = video1Checks.map((v, idx) => (idx === i ? !v : v));
    setVideo1Checks(next);
    saveProgress(next, video2Checks, mt5);
    if (!video1Done && next.every(Boolean)) {
      toast.success("Video 1 abgeschlossen — Video 2 freigeschaltet!");
      setTimeout(() => video2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  };

  const toggle2 = (i) => () => {
    if (!video1Done) return;
    // Step index 2 ("Account-Nummer notieren") requires a valid MT5 number
    // BEFORE the user can tick it as done. This catches the most common
    // support issue: customers ticking the step without filling the field.
    if (i === 2 && !video2Checks[2] && !mt5Valid) {
      toast.error("Bitte trage zuerst deine MT5 Account-Nummer ein (z. B. 37320083).");
      // Scroll the field into view & focus it.
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="mt5-input"]');
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
      });
      return;
    }
    const next = video2Checks.map((v, idx) => (idx === i ? !v : v));
    setVideo2Checks(next);
    saveProgress(video1Checks, next, mt5);
    if (!video2Done && next.every(Boolean)) {
      toast.success("Letzter Schritt geschafft — du bist startklar!");
      setTimeout(() => completionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  };

  // If the user wipes out a previously-valid MT5 number AFTER ticking the
  // "Account-Nummer notieren" step, un-tick that step automatically so the
  // progress reflects reality and the completion CTA is correctly gated.
  // We read live state via `liveRef` to avoid a stale-closure bug — if we
  // were to close over video1Checks/video2Checks/mt5 directly with a sparse
  // dep array, we could overwrite later toggles with an outdated checks
  // array (the classic "my tick disappeared after typing" footgun).
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (mt5Valid) return;
    const { video1Checks: v1, video2Checks: v2, mt5: m } = liveRef.current;
    if (!v2[2]) return;
    const next = [...v2];
    next[2] = false;
    setVideo2Checks(next);
    saveProgress(v1, next, m);
  }, [mt5Valid, saveProgress]);

  const onMt5Change = (e) => {
    const v = e.target.value.replace(/\s+/g, "");
    setMt5(v);
    saveProgress(video1Checks, video2Checks, v);
  };
  const onMt5Blur = () => {
    // Force-flush any pending debounced save so the number lands on the
    // server even if the user immediately leaves the tab afterwards.
    flushSaveImmediate();
  };

  const copyPartnerSlug = () => {
    if (!partnerSlug) return;
    navigator.clipboard.writeText(partnerSlug);
    toast.success("Partner-ID kopiert!");
  };

  if (user === null) {
    return <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center text-slate-500">Lade…</div>;
  }
  if (!user || !user.id) return null;

  // SHORTCUT VIEW — for partners/customers who are already onboarded on
  // RoboForex and shouldn't see videos/checklist. Admin flag set via
  // POST /api/admin/customers/{id}/skip-onboarding.
  if (user.skip_onboarding) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)]" data-testid="onboarding-page">
        <Header user={user} onLogout={async () => { await logout(); navigate("/"); }} />
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-24">
          <section className="al-card p-8 sm:p-12 text-center" data-testid="skip-onboarding-card">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-200">
              <Trophy className="h-8 w-8" />
            </div>
            <h1 className="mt-6 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Willkommen, {user.first_name}.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl mx-auto">
              Dein Konto ist freigegeben. Du bist bereits bei RoboForex eingerichtet — den
              Onboarding-Prozess musst du daher nicht mehr durchlaufen.
            </p>
            <p className="mt-2 text-sm text-slate-500 max-w-xl mx-auto">
              Klick einfach auf den Button unten, um zu deinem persönlichen Dashboard zu wechseln.
            </p>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              data-testid="skip-onboarding-dashboard-btn"
              className="al-cta mt-7 inline-flex justify-center"
            >
              Zum Dashboard <ArrowRight className="h-4 w-4" />
            </button>

            {partnerName && (
              <p className="mt-7 text-xs text-slate-500">
                Empfohlen durch <strong className="text-slate-700">{partnerName}</strong>
              </p>
            )}
          </section>
        </main>
      </div>
    );
  }

  // Extra UI slots injected into specific Video 1 / Video 2 checklist items
  const video1Extras = {
    1: partnerSlug ? <PartnerIdHighlight slug={partnerSlug} label="Partner-ID:" /> : null,
  };
  const video2Extras = {
    1: partnerSlug ? <PartnerIdHighlight slug={partnerSlug} label="Partners Agent ID:" /> : null,  // step 2 — MT5-Account-Einstellungen mit Partner-Slug
    2: (  // step 3 — „Account-Nummer notieren"
      <Mt5Inline
        value={mt5}
        onChange={onMt5Change}
        onBlur={onMt5Blur}
        valid={mt5Valid}
        contactLabel={contactLabel}
        contactWa={contactWa}
        contactTel={contactTel}
      />
    ),
    3: <DepositOptionsBox />,  // step 4 — „Geld auf MT5 Konto einzahlen"
    4: isGubnr ? null : (  // step 5 — „Bot verknüpfen via Copy-Trading-Link"
      <PartnerMt5IdBanner
        mt5Id={partnerMt5Id}
        partnerSlug={partnerSlug}
        partnerName={partnerName}
        contactWa={contactWa}
        contactTel={contactTel}
      />
    ),
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="onboarding-page">
      <Header user={user} onLogout={async () => { await logout(); navigate("/"); }} />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero + progress */}
        <section className="mb-10 sm:mb-14" data-testid="onboarding-hero">
          <span className="al-tag">Hallo, {user.first_name}</span>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
            Willkommen bei <span className="al-grad-text">Alphalane.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Wir begleiten dich Schritt für Schritt durch dein Setup.
          </p>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3" data-testid="tip-box">
            <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <strong>Tipp:</strong> Spiele das Video am besten auf deinem PC ab und führe die Registrierung parallel auf deinem Smartphone durch.
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-8" data-testid="progress-section">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Fortschritt</div>
              <div className="font-mono-tab text-2xl sm:text-3xl font-bold al-grad-text" data-testid="progress-percent">{percent}%</div>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full al-grad-bg transition-all duration-700 ease-out rounded-full"
                style={{ width: `${percent}%` }}
                data-testid="progress-bar"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <div className="text-slate-600">{motivationText}</div>
              <div className="text-slate-400 text-xs font-mono-tab">
                {completedSteps}/{totalSteps} Schritte
                {savingProgress && <span className="ml-2 text-[var(--al-purple)]">· Speichere…</span>}
              </div>
            </div>
          </div>

          {partnerSlug && (
            <div className="mt-6 rounded-2xl border border-[var(--al-purple)]/20 bg-[var(--al-grad-soft)] al-grad-soft-bg px-5 py-4 flex items-center gap-4" data-testid="partner-id-box">
              <Sparkles className="h-5 w-5 text-[var(--al-purple)]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Deine Partner-ID</div>
                <div className="mt-1 flex items-center gap-3">
                  <span className="font-mono-tab font-bold text-xl text-slate-900" data-testid="partner-id-value">{partnerSlug}</span>
                  <button onClick={copyPartnerSlug} className="al-cta-ghost !py-1.5 !px-3 text-xs" data-testid="copy-partner-id">
                    <Copy className="h-3.5 w-3.5" /> Kopieren
                  </button>
                </div>
                {partnerName && <div className="mt-1 text-xs text-slate-600">Empfohlen durch {partnerName}</div>}
              </div>
            </div>
          )}
        </section>

        {/* VIDEO 1 SECTION */}
        <section className="mb-12 sm:mb-16" data-testid="video1-section">
          <SectionHeader
            number="1"
            title="Deine nächsten Schritte"
            subtitle="Registriere dich beim Broker, aktiviere 2FA und schließe die Identitätsverifikation ab. Als Hilfestellung dient untenstehendes Video sowie die Schritt-für-Schritt Anleitung."
            done={video1Done}
          />

          <div className="al-card overflow-hidden mt-6">
            <YouTubeEmbed videoId={VIDEO1_ID} testid="video1-embed" />
          </div>

          {partnerSlug && (
            <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-start gap-3">
              <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                Bitte gib im Registrierungsformular folgende Partner-ID ein: <strong className="font-mono-tab text-base">{partnerSlug}</strong>
              </div>
            </div>
          )}

          <Checklist items={VIDEO1_STEPS} checks={video1Checks} onToggle={toggle1} idPrefix="v1" extras={video1Extras} />
        </section>

        {/* VIDEO 2 SECTION */}
        <section ref={video2Ref} className="mb-12 sm:mb-16" data-testid="video2-section">
          <SectionHeader
            number="2"
            title="Trading-Account & Copy Trading verbinden"
            subtitle="MT5 ProCent eröffnen, Geld transferieren und das Copy-Trading verbinden."
            locked={!video1Done}
            done={video2Done}
          />

          {!video1Done ? (
            <div className="mt-6 al-card p-10 text-center bg-slate-50/50" data-testid="video2-locked">
              <Lock className="h-10 w-10 text-slate-300 mx-auto" />
              <h3 className="mt-4 font-display font-semibold text-lg text-slate-700">Schließe zuerst alle Schritte aus Video 1 ab.</h3>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                Sobald du oben alle {VIDEO1_LEN} Schritte abgehakt hast, schaltet sich Video 2 hier automatisch frei.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono-tab text-slate-400">
                {video1Checks.filter(Boolean).length} / {video1Checks.length} Schritte erledigt
              </div>
            </div>
          ) : (
            <>
              <div className="al-card overflow-hidden mt-6">
                <YouTubeEmbed videoId={video2Id} testid="video2-embed" />
              </div>

              <Checklist items={video2Steps} checks={video2Checks} onToggle={toggle2} idPrefix="v2" extras={video2Extras} />
            </>
          )}
        </section>

        {/* COMPLETION SCREEN */}
        {allDone && (
          <section ref={completionRef} className="mb-12 sm:mb-16" data-testid="completion-section">
            <div className="al-card p-8 sm:p-12 text-center bg-gradient-to-br from-emerald-50 via-white to-purple-50 border-2 border-emerald-200">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-emerald-200">
                <Trophy className="h-8 w-8" />
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl">
                Herzlichen Glückwunsch!
              </h2>
              <p className="mt-4 text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
                Dein Onboarding wurde erfolgreich abgeschlossen.
              </p>
              <p className="mt-2 text-base text-slate-600 max-w-2xl mx-auto">
                Dein Trading-System wird nun vorbereitet. Wir melden uns innerhalb der nächsten 24 Stunden mit den finalen Details.
              </p>

              {/* WhatsApp Fast-Track CTA */}
              <div className="mt-7 rounded-2xl border-2 border-emerald-300 bg-white/80 backdrop-blur-sm p-5 sm:p-6 max-w-xl mx-auto text-left" data-testid="completion-wa-cta">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                      Schneller freigeschaltet werden
                    </div>
                    <p className="mt-1 text-sm sm:text-base text-slate-700 leading-relaxed">
                      Schick deinem Empfehlungsgeber jetzt kurz eine <strong>WhatsApp</strong>, damit du
                      so schnell wie möglich live geschaltet werden kannst.
                    </p>
                  </div>
                </div>
                <a
                  href={`${contactWa}${contactWa.includes("?") ? "&" : "?"}text=${encodeURIComponent(
                    "Hi! Ich habe alle Schritte des Onboardings durchgeführt und die Anfrage auf RoboForex gestellt. Wie geht es nun weiter?"
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="completion-wa-button"
                  className="mt-4 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-emerald-500 text-white font-display font-bold text-sm sm:text-base hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-200"
                >
                  <MessageCircle className="h-5 w-5" />
                  Empfehlungsgeber jetzt per WhatsApp informieren
                </a>
                <p className="mt-2 text-[11px] text-slate-500 text-center">
                  Öffnet WhatsApp mit einer vorgefertigten Nachricht — du musst nur noch auf „Senden" tippen.
                </p>
              </div>
            </div>

            {/* Q&A community invite */}
            <div className="mt-5">
              <QACommunityBanner dismissible={false} />
            </div>

            {/* Dashboard CTA — Onboarding via WhatsApp Live Community */}
            <div className="mt-5 al-card p-6 sm:p-7 border-2 border-violet-200 bg-gradient-to-br from-violet-50/70 via-white to-blue-50/60" data-testid="completion-dashboard-cta">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center flex-shrink-0">
                  <LayoutDashboard className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-violet-700">
                    Dein persönliches Dashboard
                  </div>
                  <p className="mt-1 text-sm sm:text-base text-slate-700 leading-relaxed">
                    Richte dein Dashboard zunächst anhand der <strong>Anleitung</strong> ein
                    (MyFxBook-Verbindung) — danach siehst du Equity, Trades &amp; Gewinne live.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="completion-dashboard-button"
                className="mt-4 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full al-grad-bg text-white font-display font-bold text-sm sm:text-base hover:opacity-90 hover:shadow-2xl transition-all shadow-lg shadow-violet-300/60"
              >
                <LayoutDashboard className="h-5 w-5" />
                Dashboard öffnen
              </button>
            </div>

            {/* Post-completion follow-up reminder */}
            <div
              className="mt-5 al-card p-6 sm:p-7 border-2 border-amber-200 bg-amber-50/60"
              data-testid="post-completion-reminder"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700">
                    In ein paar Stunden — letzter Check
                  </div>
                  <p className="mt-1.5 text-sm sm:text-base text-slate-800 leading-relaxed">
                    Prüfe in ein paar Stunden unter <strong>„My Copy Trading"</strong> in deinem
                    RoboForex-Account, ob das Copy Trading <strong>aktiv</strong> ist. Sobald wir
                    dich freigeschaltet haben, beginnt die Verbindung automatisch zu arbeiten.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ADD TO HOME SCREEN */}
        <section className="mb-12 sm:mb-16" data-testid="add-to-home-section">
          <div className="al-card p-7 sm:p-9 bg-gradient-to-br from-violet-50 via-white to-blue-50 border-2 border-violet-200">
            <div className="flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-2xl bg-[var(--al-purple)] text-white flex items-center justify-center shadow-lg shadow-violet-300 mb-4">
                <Smartphone className="h-7 w-7" />
              </div>
              <span className="text-xs uppercase tracking-widest font-bold text-[var(--al-purple)]">Schnellzugriff</span>
              <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl text-slate-900">
                Dein Dashboard auf dem Handy als App
              </h2>
              <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
                Speichere dir das Dashboard direkt auf den Home-Bildschirm — so öffnest du es mit einem Tippen wie eine native App, ohne Browser, ohne Login-Faff.
              </p>

              {/* iOS + Android Steps */}
              <div className="mt-6 grid sm:grid-cols-2 gap-6 w-full text-left" data-testid="add-to-home-steps">
                <StepCard
                  platform="iPhone / iPad (Safari)"
                  icon="🍎"
                  steps={[
                    <>Öffne <strong>alphalane-tradingsystems.com/dashboard</strong> in <strong>Safari</strong>.</>,
                    <>Tippe unten in der Leiste auf das <strong>Teilen-Symbol</strong> <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-slate-200 text-slate-700"><Share className="h-3 w-3" /></span> (Quadrat mit Pfeil nach oben).</>,
                    <>Scrolle nach unten und tippe auf <strong>„Zum Home-Bildschirm"</strong> <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-slate-200 text-slate-700"><Plus className="h-3 w-3" /></span>.</>,
                    <>Bestätige mit <strong>„Hinzufügen"</strong> oben rechts — fertig 🎉</>,
                  ]}
                />
                <StepCard
                  platform="Android (Chrome)"
                  icon="🤖"
                  steps={[
                    <>Öffne <strong>alphalane-tradingsystems.com/dashboard</strong> in <strong>Chrome</strong>.</>,
                    <>Tippe oben rechts auf die <strong>drei Punkte</strong> ⋮.</>,
                    <>Wähle <strong>„App installieren"</strong> oder <strong>„Zum Startbildschirm hinzufügen"</strong>.</>,
                    <>Bestätige mit <strong>„Installieren"</strong> — fertig 🎉</>,
                  ]}
                />
              </div>

              <p className="mt-5 text-xs text-slate-500 italic">
                Tipp: Nach dem Hinzufügen erscheint Alphalane mit Logo wie eine echte App auf deinem Home-Bildschirm.
              </p>
            </div>
          </div>
        </section>

        {/* WHATSAPP CHANNEL */}
        <section className="mb-12 sm:mb-16" data-testid="whatsapp-section">
          <div className="al-card p-7 sm:p-9 bg-gradient-to-br from-[#25D366]/8 via-white to-[#128C7E]/8 border-2 border-[#25D366]/30">
            <div className="flex items-start gap-5 flex-wrap">
              <div className="h-14 w-14 rounded-2xl bg-[#25D366] text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#25D366]/30">
                <MessageCircle className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs uppercase tracking-widest font-bold text-[#128C7E]">Bleibe informiert</span>
                <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl text-slate-900">WhatsApp-Channel beitreten</h2>
                <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-xl leading-relaxed">
                  Trete jetzt unserem WhatsApp Channel bei, um wichtige Updates und Informationen zu erhalten — direkt aufs Handy, ohne zu suchen.
                </p>
                <a
                  href={WHATSAPP_CHANNEL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-cta"
                  className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#1ea855] text-white font-display font-semibold transition-all duration-300 shadow-lg shadow-[#25D366]/30 hover:shadow-xl hover:shadow-[#25D366]/40 hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Channel beitreten
                  <ExternalLink className="h-4 w-4 opacity-80" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK LINKS */}
        <section className="mb-12 sm:mb-16">
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-2">Wichtige Links</h2>
          <p className="text-sm text-slate-600 mb-7 max-w-xl">Alles, was du regelmäßig brauchen wirst.</p>

          {/* Featured link: Persönliches Dashboard */}
          <Link
            to="/dashboard"
            data-testid="link-dashboard"
            className="group block mb-4 rounded-2xl al-grad-bg text-white p-5 sm:p-7 shadow-xl shadow-violet-500/30 hover:shadow-2xl hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at 90% 20%, rgba(255,255,255,0.5), transparent 50%)" }} />
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center flex-shrink-0">
                <LayoutDashboard className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-white/70 mb-1">
                  Dein persönlicher Bereich
                </div>
                <div className="font-display font-bold text-xl sm:text-2xl leading-tight">
                  Persönliches Dashboard öffnen
                </div>
                <div className="text-sm text-white/80 mt-1.5 hidden sm:block">
                  Equity, Trades & Live-Daten — alles auf einen Blick.
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[var(--al-purple)] font-display font-semibold text-sm group-hover:gap-3 transition-all flex-shrink-0">
                Öffnen <ChevronRight className="h-4 w-4" />
              </div>
              <ChevronRight className="sm:hidden h-6 w-6 text-white/80 flex-shrink-0" />
            </div>
          </Link>

          <div className="grid sm:grid-cols-2 gap-4" data-testid="onboarding-links">
            <QuickLink icon={<BarChart3 className="h-5 w-5" />} title="System Track Record" subtitle="Live-Performance, tagesaktuell" href="/#track-record" testid="link-track-record" />
            <QuickLink icon={<BookOpen className="h-5 w-5" />} title="Alphalane Blog" subtitle="Wissen rund um KI-Trading" href="/blog" testid="link-blog" />
            <QuickLink icon={<FileText className="h-5 w-5" />} title="AGB & Datenschutz" subtitle="Vertragsdokumente einsehen" href="/agb" testid="link-agb" />
            <QuickLink icon={<MessagesSquare className="h-5 w-5" />} title="WhatsApp Channel" subtitle="Updates aufs Handy" href={WHATSAPP_CHANNEL} testid="link-whatsapp" />
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-2 flex items-center gap-3">
            <MessagesSquare className="h-6 w-6 text-[var(--al-purple)]" />
            Häufige Fragen
          </h2>
          <p className="text-sm text-slate-600 mb-7 max-w-xl">Die häufigsten Fragen rund um Setup &amp; Verifikation.</p>
          <Faq openFaq={openFaq} setOpenFaq={setOpenFaq} contactLabel={contactLabel} contactWa={contactWa} contactTel={contactTel} />
        </section>

        {/* DIRECT HELP — kontakt zum empfehlungsgeber */}
        <section className="al-card p-7 sm:p-9 al-grad-soft-bg" data-testid="onboarding-help">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full al-grad-bg text-white flex items-center justify-center flex-shrink-0">
              <Wrench className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-xl sm:text-2xl">Hängt etwas?</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Bei Fragen oder Problemen wende dich direkt an <strong>{contactLabel}</strong> — schnelle Hilfe ist nur einen Klick entfernt.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {contactWa && (
                  <a
                    href={contactWa}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="help-whatsapp"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1ea855] text-white font-display font-semibold transition-all duration-300 shadow-lg shadow-[#25D366]/30 hover:shadow-xl hover:-translate-y-0.5"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp schreiben
                  </a>
                )}
                <a href={contactTel} className="al-cta-ghost" data-testid="help-call" aria-label="Anrufen">
                  <Phone className="h-4 w-4" /> Anrufen
                </a>
                {partnerEmail ? (
                  <a href={`mailto:${partnerEmail}`} className="al-cta-ghost" data-testid="help-mail">
                    <Mail className="h-4 w-4" /> Mail an {contactLabel}
                  </a>
                ) : (
                  <a href={`mailto:${EMAIL}`} className="al-cta-ghost" data-testid="help-mail">
                    <Mail className="h-4 w-4" /> Mail schreiben
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[var(--al-line)] bg-white/60 mt-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-3">
          <div>© Alphalane Trading Systems · Moonshine Solutions GmbH · Schmitten FR, Schweiz</div>
          <Link to="/" className="hover:text-slate-900">Zurück zur Website →</Link>
        </div>
      </footer>
    </div>
  );
}

// ----------------------- Sub-components -----------------------
const Header = ({ user, onLogout }) => (
  <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
      <Link to="/" className="flex items-center gap-3">
        <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
        <div className="font-display font-semibold text-[15px]">
          <span className="al-grad-text">ALPHALANE</span>
          <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest hidden sm:inline">Onboarding</span>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        <div className="hidden sm:block text-xs text-right">
          <div className="font-semibold text-slate-900">{user.first_name} {user.last_name}</div>
          <div className="text-slate-500">{user.email}</div>
        </div>
        <button onClick={onLogout} className="al-cta-ghost !py-2 !px-3 text-sm" data-testid="logout-btn">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  </header>
);

const SectionHeader = ({ number, title, subtitle, locked, done }) => (
  <div className="flex items-start gap-4">
    <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center font-display font-bold text-lg transition-all ${
      done ? "bg-emerald-500 text-white" : locked ? "bg-slate-200 text-slate-400" : "al-grad-bg text-white"
    }`}>
      {done ? <Check className="h-6 w-6" /> : locked ? <Lock className="h-5 w-5" /> : number}
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 leading-tight">{title}</h2>
      <p className="mt-2 text-sm sm:text-base text-slate-600">{subtitle}</p>
    </div>
  </div>
);

const YouTubeEmbed = ({ videoId, testid }) => (
  <div className="relative w-full bg-slate-900" style={{ aspectRatio: "16 / 9" }} data-testid={testid}>
    <iframe
      src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
      title="Alphalane Onboarding"
      className="absolute inset-0 w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const Checklist = ({ items, checks, onToggle, idPrefix, extras = {} }) => (
  <div className="mt-6 al-card divide-y divide-[var(--al-line)] overflow-hidden" data-testid={`${idPrefix}-checklist`}>
    {items.map((item, i) => {
      const checked = !!checks[i];
      const extra = extras[i];
      const isObj = typeof item !== "string";
      const text = isObj ? item.text : item;
      const title = isObj ? item.title : null;
      const cta = isObj ? item.cta : null;
      return (
        <div key={`${idPrefix}-step-${i}-${typeof item === "string" ? item.slice(0, 24) : (item.title || item.text || "").slice(0, 24)}`} data-testid={`${idPrefix}-step-${i}-row`}>
          <button
            type="button"
            onClick={onToggle(i)}
            data-testid={`${idPrefix}-step-${i}`}
            className={`w-full text-left px-5 py-4 flex items-start gap-4 transition-all ${checked ? "bg-emerald-50/40" : "hover:bg-slate-50"}`}
          >
            <div className={`flex-shrink-0 mt-0.5 h-6 w-6 rounded-md border-2 flex items-center justify-center transition-all ${
              checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300 group-hover:border-[var(--al-purple)]"
            }`}>
              {checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono-tab font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</div>
              {title && (
                <div className={`mt-0.5 font-display font-bold text-base sm:text-lg ${checked ? "text-slate-500 line-through" : "text-slate-900"}`}>
                  {title}
                </div>
              )}
              <div className={`text-sm sm:text-[15px] leading-relaxed ${title ? "mt-1" : "mt-0.5"} ${checked ? "text-slate-400 line-through" : "text-slate-700"}`}>
                {text}
              </div>
            </div>
          </button>
          {cta && (
            <div className="px-5 pb-4 pl-[60px]">
              <a
                href={cta.href}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`${idPrefix}-step-${i}-cta`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full al-grad-bg text-white text-sm font-display font-semibold hover:opacity-90 transition-opacity shadow-sm"
              >
                <ExternalLink className="h-4 w-4" /> {cta.label}
              </a>
            </div>
          )}
          {extra && (
            <div className="px-5 pb-5 pl-[60px]" data-testid={`${idPrefix}-step-${i}-extra`}>
              {extra}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const PartnerIdHighlight = ({ slug, label }) => (
  <div className="rounded-xl border border-[var(--al-purple)]/30 bg-[var(--al-grad-soft)] al-grad-soft-bg px-4 py-3 flex items-center gap-3" data-testid="step-partner-id-highlight">
    <Sparkles className="h-4 w-4 text-[var(--al-purple)] flex-shrink-0" />
    <div className="text-sm">
      <span className="text-slate-600">{label}</span>{" "}
      <strong className="font-mono-tab text-base text-slate-900">{slug}</strong>
    </div>
  </div>
);

// ── Partner's MT5 / Copy-Trading registration link (Video 2 setup) ─────
// Each partner has their OWN individual CopyFX URL, configured in the
// partner admin/dashboard via the "Reflink Video 2" field. Customers see THIS
// link under Video 2 — it's different from the partner_slug they used in
// Video 1 (which was the standard account opening reflink).
const PartnerMt5IdBanner = ({ mt5Id, partnerSlug, partnerName, contactWa, contactTel }) => {
  const has = !!mt5Id;
  const isUrl = has && /^https?:\/\//i.test(mt5Id);
  const copy = () => {
    if (!has) return;
    navigator.clipboard.writeText(mt5Id);
    toast.success("Link kopiert!");
  };
  if (!has) {
    return (
      <div
        className="mt-5 rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 p-5 sm:p-6 shadow-lg shadow-amber-100"
        data-testid="partner-mt5-id-banner-empty"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-md">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-block px-2.5 py-1 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-widest mb-1.5">
              Copy-Trading-Link fehlt noch
            </div>
            <h4 className="font-display font-bold text-lg sm:text-xl text-amber-950 leading-snug">
              {partnerName ? `Dein Empfehlungsgeber ${partnerName} hat den Copy-Trading-Link noch nicht hinterlegt.` : "Der Copy-Trading-Link ist noch nicht hinterlegt."}
            </h4>
            <p className="mt-2 text-sm text-amber-900 leading-relaxed">
              Du brauchst den <strong>persönlichen Copy-Trading-Link</strong> deines Empfehlungsgebers, um das Copy-Trading korrekt zu verbinden.
              Frag bitte kurz nach:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {contactWa && (
                <a
                  href={contactWa}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-mt5-empty-wa"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {contactTel && (
                <a
                  href={contactTel}
                  data-testid="partner-mt5-empty-call"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold transition-colors"
                >
                  <Phone className="h-3.5 w-3.5" /> Anrufen
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="mt-5 rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 p-5 sm:p-6 shadow-lg shadow-amber-100"
      data-testid="partner-mt5-id-banner"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-md animate-pulse">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="inline-block px-2.5 py-1 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-widest mb-1.5">
            Wichtig — bitte aufmerksam lesen
          </div>
          <h4 className="font-display font-bold text-lg sm:text-xl text-amber-950 leading-snug">
            {isUrl
              ? "Nutze diesen Copy-Trading-Link — NICHT den aus Video 1"
              : "Hier brauchst du eine ANDERE Agent-ID als in Video 1"}
          </h4>
        </div>
      </div>

      <div className="rounded-xl bg-white border-2 border-amber-300 p-4 sm:p-5">
        {isUrl ? (
          <>
            <div className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-3">
              Dein persönlicher Copy-Trading-Anmeldelink:
            </div>
            <a
              href={mt5Id}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="partner-mt5-id-open"
              className="al-grad-bg text-white inline-flex items-center justify-center gap-2 w-full px-5 py-4 rounded-xl font-display font-bold text-base sm:text-lg shadow-lg shadow-amber-200/60 hover:shadow-xl hover:scale-[1.02] transition-all"
            >
              <ExternalLink className="h-5 w-5" />
              <span>Jetzt für Copy-Trading registrieren</span>
              <ArrowRight className="h-5 w-5" />
            </a>
            <div className="mt-3 flex items-center justify-between gap-2">
              <code
                className="flex-1 min-w-0 truncate font-mono-tab text-[11px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1.5 rounded select-all"
                data-testid="partner-mt5-id-value"
              >
                {mt5Id}
              </code>
              <button
                type="button"
                onClick={copy}
                data-testid="partner-mt5-id-copy"
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold transition-colors"
              >
                <Copy className="h-3 w-3" /> Kopieren
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-2">
              Trage als <span className="text-slate-900">&bdquo;Partners Agent ID&ldquo;</span> ein:
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div
                className="font-mono-tab font-bold text-3xl sm:text-4xl tracking-wider bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent select-all"
                data-testid="partner-mt5-id-value"
              >
                {mt5Id}
              </div>
              <button
                type="button"
                onClick={copy}
                data-testid="partner-mt5-id-copy"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors shadow-sm"
              >
                <Copy className="h-3.5 w-3.5" /> Kopieren
              </button>
            </div>
          </>
        )}
        {partnerName && (
          <div className="mt-3 text-[11px] text-amber-700">
            Persönlicher Link von <strong>{partnerName}</strong>
          </div>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-1">
            ✗ NICHT verwenden
          </div>
          <div className="text-red-900">
            {partnerSlug ? (
              <>Den Link aus Video 1 (RoboForex-Konto-Eröffnung mit ID <strong className="font-mono-tab">{partnerSlug}</strong>).</>
            ) : (
              <>Den Link, den du in Video 1 verwendet hast.</>
            )}
          </div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">
            ✓ Hier verwenden
          </div>
          <div className="text-emerald-900">
            {isUrl
              ? "Den orangenen Button oben — er führt direkt zur richtigen Copy-Trading-Anmeldung."
              : <>Die ID <strong className="font-mono-tab">{mt5Id}</strong> — nur damit funktioniert das Copy-Trading korrekt.</>}
          </div>
        </div>
      </div>
    </div>
  );
};

const PartnerMt5IdHighlight = ({ mt5Id, partnerName, contactWa }) => {
  if (!mt5Id) {
    return (
      <div
        className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3"
        data-testid="partner-mt5-id-highlight-empty"
      >
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
        <div className="text-sm flex-1 text-amber-900">
          {partnerName ? `Frag ${partnerName}` : "Frag deinen Empfehlungsgeber"} nach dem Copy-Trading-Link.
        </div>
        {contactWa && (
          <a
            href={contactWa}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors"
          >
            <MessageCircle className="h-3 w-3" /> WA
          </a>
        )}
      </div>
    );
  }
  const isUrl = /^https?:\/\//i.test(mt5Id);
  if (isUrl) {
    return (
      <a
        href={mt5Id}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="partner-mt5-id-highlight"
        className="block rounded-xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3 hover:from-amber-100 hover:to-orange-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ExternalLink className="h-4 w-4 text-amber-700 flex-shrink-0" />
          <div className="text-sm flex-1 min-w-0">
            <div className="text-amber-900 font-semibold">Copy-Trading-Anmeldung öffnen</div>
            <div className="text-[11px] text-amber-700">(persönlicher Link von {partnerName || "deinem Empfehlungsgeber"})</div>
          </div>
          <ArrowRight className="h-4 w-4 text-amber-700 flex-shrink-0" />
        </div>
      </a>
    );
  }
  const copy = () => {
    navigator.clipboard.writeText(mt5Id);
    toast.success(`"${mt5Id}" kopiert!`);
  };
  return (
    <div
      className="rounded-xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3 flex items-center gap-3"
      data-testid="partner-mt5-id-highlight"
    >
      <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
      <div className="text-sm flex-1">
        <span className="text-amber-900 font-medium">Partners Agent ID:</span>{" "}
        <strong className="font-mono-tab text-base text-amber-950 select-all">{mt5Id}</strong>
        <span className="text-xs text-amber-700 ml-2">(nicht die aus Video 1!)</span>
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid="partner-mt5-id-highlight-copy"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
};

const DepositOptionsBox = () => (
  <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-4" data-testid="deposit-options-box">
    <div className="text-sm font-display font-semibold text-slate-900 mb-2">
      So findest du die Einzahlungsoptionen:
    </div>
    <div className="text-sm text-slate-700 mb-3">
      <strong>Startseite</strong> → Button <strong>„Einzahlen"</strong> beim <strong>MT5 ProCent</strong>
    </div>
    <div className="grid sm:grid-cols-3 gap-2">
      <div className="rounded-lg bg-white border border-blue-100 px-3 py-2.5 flex items-center gap-2 text-sm">
        <CreditCard className="h-4 w-4 text-blue-600 flex-shrink-0" /> Kreditkarte
      </div>
      <div className="rounded-lg bg-white border border-blue-100 px-3 py-2.5 flex items-center gap-2 text-sm">
        <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" /> Banküberweisung
      </div>
      <div className="rounded-lg bg-white border border-blue-100 px-3 py-2.5 flex items-center gap-2 text-sm">
        <Bitcoin className="h-4 w-4 text-blue-600 flex-shrink-0" /> Crypto
      </div>
    </div>
  </div>
);

const Mt5Inline = ({ value, onChange, onBlur, valid, contactLabel, contactWa, contactTel }) => {
  const hasContent = (value || "").trim().length > 0;
  // Show validation error only AFTER the user has typed something (so the
  // field doesn't scream "invalid" the very moment they see it).
  const showError = hasContent && !valid;
  const showSuccess = hasContent && valid;
  return (
    <div
      className={`rounded-xl border bg-white px-4 py-4 transition ${
        showError
          ? "border-rose-300 ring-2 ring-rose-100"
          : showSuccess
          ? "border-emerald-300 ring-2 ring-emerald-100"
          : "border-[var(--al-purple)]/30"
      }`}
      data-testid="mt5-inline"
    >
      <label className="block">
        <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Deine MT5 Account-Nummer hier eintragen</span>
        <input
          type="text"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="z.B. 37320083"
          pattern="[0-9]+"
          inputMode="numeric"
          maxLength={12}
          data-testid="mt5-input"
          className={`mt-2 w-full rounded-xl border bg-white px-4 py-3 text-base font-mono-tab focus:outline-none focus:ring-2 transition ${
            showError
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-200"
              : "border-[var(--al-line)] focus:border-[var(--al-purple)] focus:ring-[var(--al-purple)]/20"
          }`}
        />
      </label>
      {showError ? (
        <p className="mt-2 text-xs text-rose-700 leading-relaxed" data-testid="mt5-error">
          Bitte trage deine vollständige MT5 Account-Nummer ein (nur Zahlen, {MT5_MIN}–{MT5_MAX} Stellen,
          z. B. <span className="font-mono-tab font-semibold">37320083</span>).
        </p>
      ) : showSuccess ? (
        <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="mt5-ok">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" /> MT5-Nummer sieht gut aus.
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Wichtig für die Zuordnung — bitte nach Account-Erstellung sofort eintragen.
        </p>
      )}
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 leading-relaxed">
        <strong className="text-slate-800">Du findest deine Nummer nicht?</strong>{" "}
        Schreib bitte direkt an{" "}
        {contactWa && (
          <a href={contactWa} target="_blank" rel="noreferrer" className="text-[var(--al-purple)] font-semibold hover:underline">
            {contactLabel}
          </a>
        )}
        {contactWa && contactTel ? " oder " : ""}
        {contactTel && (
          <a href="https://wa.me/41799501646" target="_blank" rel="noreferrer" className="text-[var(--al-purple)] font-semibold hover:underline">
            Pascal Schwab
          </a>
        )}
        {" — wir helfen dir sofort weiter."}
      </div>

      {/* Investor-Passwort wird ausschließlich im Dashboard eingegeben — nicht hier im Onboarding. */}
    </div>
  );
};

const Faq = ({ openFaq, setOpenFaq, contactLabel, contactWa, contactTel }) => {
  const items = [
    {
      q: "Wie lange dauert der gesamte Setup-Prozess?",
      a: "In der Regel 3–5 Werktage. Größter Zeitfresser ist meist die Broker-Verifikation.",
    },
    {
      q: "Was, wenn die Verifikation länger als 3 Tage dauert?",
      a: "Antworte auf eine unserer Mails oder ruf uns an — wir kontaktieren den Broker direkt und kümmern uns.",
    },
    {
      q: "An wen wende ich mich bei Problemen?",
      a: "contact-block",  // special marker
    },
    {
      q: "Was bedeutet „Proportional\" und Koeffizient 1?",
      a: "„Proportional\" stellt sicher, dass die Trades im gleichen Verhältnis zu deinem Kapital kopiert werden. Koeffizient 1 = 1:1 Kopie der Master-Position relativ zum eingesetzten Kapital.",
    },
    {
      q: "Was passiert, wenn ich pausieren oder kündigen will?",
      a: "Du kannst Copy Trading jederzeit direkt in deinem Tradingaccount auf Roboforex stoppen — dafür brauchst du uns nicht. Dein Konto, dein Geld, deine Kontrolle. Es gibt keine Vertragslaufzeit und keine Kündigungsfrist.",
    },
    {
      q: "Wann sehe ich die ersten Trades auf meinem Konto?",
      a: "Sobald Copy Trading aktiv ist (kann ein paar Stunden dauern), übernimmt das System automatisch. Trades werden in 1:1-Echtzeit kopiert — sobald wir handeln, handelst du. Im Dashboard siehst du jederzeit, was läuft.",
    },
    {
      q: "Was passiert mit den Gewinnen — werden sie automatisch ausgezahlt?",
      a: "Gewinne bleiben auf deinem Tradingaccount auf Roboforex. Auszahlungen kannst du jederzeit selbst initiieren — direkt im Roboforex-Dashboard. Du entscheidest selbst, wie viel du auszahlst und wann.",
    },
    {
      q: "Brauche ich Trading-Erfahrung?",
      a: "Nein, gar nicht. Das System ist vollständig automatisiert — du musst keine Charts analysieren, keine Trades öffnen und keine Strategien verstehen. Sobald Copy Trading aktiv ist, läuft alles im Hintergrund.",
    },

    // --- Häufige Einwände & Bedenken ---
    {
      q: "Ist mein Geld bei Alphalane sicher? Wer hat darauf Zugriff?",
      a: "Dein Geld liegt zu 100 % auf einem Tradingaccount auf DEINEM Namen bei Roboforex — einem regulierten Broker. Alphalane hat ausschliesslich eine Handels­vollmacht: Wir dürfen Trades platzieren, mehr nicht. Ein- und Auszahlungen kannst nur DU selbst durchführen. Wir können dein Kapital weder transferieren, noch entnehmen, noch sehen.",
    },
  ];
  return (
    <div className="al-card overflow-hidden divide-y divide-[var(--al-line)]" data-testid="onboarding-faq">
      {items.map((f, i) => {
        const isOpen = openFaq === i;
        return (
          <div key={f.q} data-testid={`faq-${i}`}>
            <button onClick={() => setOpenFaq(isOpen ? -1 : i)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
              <span className="font-display font-semibold text-slate-900">{f.q}</span>
              <ChevronRight className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">
                {f.a === "contact-block" ? (
                  <FaqContactBlock label={contactLabel} waLink={contactWa} telLink={contactTel} />
                ) : (
                  f.a
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const FaqContactBlock = ({ label, waLink, telLink }) => (
  <div data-testid="faq-contact-block">
    <p>
      Direkter Draht zu <strong>{label}</strong> — er hilft dir bei allen Fragen rund ums Setup und während des laufenden Tradings.
    </p>
    <div className="mt-4 flex flex-wrap gap-3">
      {waLink && (
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="faq-whatsapp"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1ea855] text-white text-sm font-display font-semibold transition-all duration-300 shadow-md hover:-translate-y-0.5"
        >
          <span className="text-base">📞</span> WhatsApp schreiben
        </a>
      )}
      {telLink && (
        <a
          href={telLink}
          data-testid="faq-call"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 hover:border-[var(--al-purple)] text-slate-700 text-sm font-display font-semibold transition-all"
        >
          <span className="text-base">📞</span> Anrufen
        </a>
      )}
    </div>
  </div>
);

const QuickLink = ({ icon, title, subtitle, href, testid }) => {
  const inner = (
    <div className="al-card p-5 flex items-center gap-4 hover:border-[var(--al-purple)]/40 transition-all group">
      <div className="h-10 w-10 rounded-xl bg-[var(--al-grad-soft)] al-grad-soft-bg text-[var(--al-purple)] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-[var(--al-purple)] transition-colors" />
    </div>
  );
  if (!href) return <div data-testid={testid}>{inner}</div>;
  if (href.startsWith("/")) return <Link to={href} data-testid={testid}>{inner}</Link>;
  return <a href={href} data-testid={testid} target="_blank" rel="noreferrer">{inner}</a>;
};

const StepCard = ({ platform, icon, steps }) => (
  <div className="rounded-2xl border border-violet-200 bg-white p-5">
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xl">{icon}</span>
      <h3 className="font-display font-semibold text-slate-900 text-sm">{platform}</h3>
    </div>
    <ol className="space-y-2.5">
      {steps.map((step, i) => (
        <li key={`${platform}-${i}`} className="flex items-start gap-3 text-sm text-slate-700 leading-snug">
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[var(--al-purple)] text-white text-[10px] font-bold tabular-nums flex-shrink-0 mt-0.5">
            {i + 1}
          </span>
          <span className="flex-1">{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

